import { useState, useCallback } from "react";
import { DotDivider } from "./DecorativePatterns";
import { DotWaterhole } from "./AboriginalPatterns";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/logo.svg";

const Footer = () => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = firstName.trim();
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedName || !trimmedEmail) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setErrorMsg("Please enter a valid email address.");
      setStatus("error");
      return;
    }

    setStatus("loading");
    setErrorMsg("");

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      const res = await fetch(`${supabaseUrl}/functions/v1/ghl-newsletter`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${anonKey}`,
          apikey: anonKey,
        },
        body: JSON.stringify({ first_name: trimmedName, email: trimmedEmail }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Something went wrong");
      }

      setStatus("success");
      setFirstName("");
      setEmail("");
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong");
      setStatus("error");
    }
  }, [firstName, email]);

  const navLinks = [
    { to: "/where-risk-forms", label: "Where Risk Forms" },
    { to: "/timing", label: "Why Timing Matters" },
    { to: "/who-this-is-for", label: "Who This Is For" },
    { to: "/about", label: "About" },
    { to: "/book-a-call", label: "Book a Call" },
  ];

  return (
    <footer className="relative border-t border-border/10 bg-card/30">
      {/* Decorative background */}
      <div className="absolute bottom-0 left-0 hidden lg:block" style={{ zIndex: 0 }}>
        <DotWaterhole className="text-primary opacity-20" size={350} />
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        {/* Main footer content */}
        <div className="py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-y-12 md:gap-x-8">

          {/* Col 1: Brand */}
          <div className="md:col-span-5">
            <img src={logo} alt="Boomerang Cultures" className="h-6 opacity-90 mb-5" width="79" height="24" />
            <p className="text-[15px] text-muted-foreground leading-relaxed max-w-sm">
              Advisory for CEOs and acquisition firms who want to address execution
              risk before it becomes visible. Discretion, judgment, and timing.
            </p>
          </div>

          {/* Col 2: Navigation */}
          <div className="md:col-span-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted-foreground mb-5">
              Explore
            </p>
            <nav className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <div key={link.to}>
                  <Link
                    to={link.to}
                    className="relative inline-block text-sm text-muted-foreground hover:text-foreground transition-all duration-300 after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-px after:bottom-[-2px] after:left-0 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-500 hover:after:scale-x-100 hover:after:origin-bottom-left"
                  >
                    {link.label}
                  </Link>
                </div>
              ))}
            </nav>
          </div>

          {/* Col 3: Newsletter */}
          <div className="md:col-span-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted-foreground/70 mb-2">
                Stay informed
              </p>
              <p className="text-sm text-muted-foreground/80 leading-relaxed mb-5">
                Occasional insights on leadership timing and execution risk. No noise.
              </p>

              {status === "success" ? (
                <div className="flex items-center gap-2.5">
                  <div className="p-1 rounded-full bg-primary/15">
                    <Check size={12} className="text-primary" />
                  </div>
                  <p className="text-sm text-foreground/80">You're on the list.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3">
                  <input
                    type="text"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    maxLength={100}
                    required
                    className="w-full px-3.5 py-2.5 rounded-lg bg-card border border-border/50 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/50 smooth"
                  />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    maxLength={255}
                    required
                    className="w-full px-3.5 py-2.5 rounded-lg bg-card border border-border/50 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/50 smooth"
                  />
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-wider bg-primary/20 text-foreground border border-primary/30 hover:bg-primary/25 hover:border-primary/40 smooth rounded-lg group hover:-translate-y-px disabled:opacity-50 disabled:pointer-events-none"
                  >
                    {status === "loading" ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <>
                        Subscribe
                        <ArrowRight size={12} className="group-hover:translate-x-0.5 smooth" />
                      </>
                    )}
                  </button>
                  {status === "error" && errorMsg && (
                    <p className="text-xs text-destructive">{errorMsg}</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-border/15 py-6 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-[11px] text-muted-foreground tracking-wide">
            © {new Date().getFullYear()} Boomerang Cultures
          </p>
          <Link
            to="/book-a-call"
            className="text-[11px] text-muted-foreground hover:text-primary smooth tracking-wide"
          >
            Book a Call
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
